import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, FileText, Download, Eye, Calendar, Filter } from 'lucide-react'
import evaluationService from '../../../services/evaluationService'
import classService from '../../../services/classService'

export function EvaluationResultsPage() {
  const navigate = useNavigate()
  const [selectedClass, setSelectedClass] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')

  const { data: evaluations, isLoading } = useQuery({
    queryKey: ['evaluations'],
    queryFn: () => evaluationService.getAll()
  })

  const { data: classes } = useQuery({
    queryKey: ['classes'],
    queryFn: () => classService.getAll()
  })

  const filteredEvaluations = evaluations?.filter((evaluation: any) => {
    if (selectedClass && evaluation.student?.classId !== selectedClass) return false
    if (selectedStatus && evaluation.status !== selectedStatus) return false
    return true
  })

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700',
      validated: 'bg-green-100 text-green-700',
      completed: 'bg-blue-100 text-blue-700',
      published: 'bg-purple-100 text-purple-700'
    }
    const labels: Record<string, string> = {
      draft: 'Brouillon',
      validated: 'Validée',
      completed: 'Terminée',
      published: 'Publiée'
    }
    return (
      <span className={`px-2 py-1 rounded-md text-xs font-medium ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/modules/evaluation')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour au module</span>
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Résultats et historique</h1>
          <p className="text-gray-600">Consultez toutes les évaluations comportementales réalisées</p>
        </div>

        {/* Filtres */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Filtrer :</span>
            </div>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
            >
              <option value="">Toutes les classes</option>
              {classes?.map((cls: any) => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
            >
              <option value="">Tous les statuts</option>
              <option value="draft">Brouillon</option>
              <option value="validated">Validée</option>
              <option value="completed">Terminée</option>
              <option value="published">Publiée</option>
            </select>
          </div>
        </div>

        {/* Liste des évaluations */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
          </div>
        ) : !filteredEvaluations || filteredEvaluations.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-100 mb-4">
              <FileText className="w-8 h-8 text-violet-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Aucune évaluation</h3>
            <p className="text-gray-600 mb-6">Commencez par évaluer une classe</p>
            <button
              onClick={() => navigate('/modules/evaluation/evaluate')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl font-medium"
            >
              <span>Évaluer une classe</span>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredEvaluations.map((evaluation: any) => (
              <div key={evaluation.id} className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {evaluation.student?.firstName} {evaluation.student?.lastName}
                      </h3>
                      {getStatusBadge(evaluation.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>{evaluation.grid?.name}</span>
                      <span>•</span>
                      <span>{evaluation.student?.class?.name || 'Classe inconnue'}</span>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(evaluation.evaluatedAt || evaluation.createdAt).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-violet-600">
                      {evaluation.finalScore?.toFixed(1) || '0.0'}/20
                    </div>
                  </div>
                </div>

                {evaluation.comment && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{evaluation.comment}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/students/${evaluation.studentId}`)}
                    className="flex items-center gap-2 px-3 py-2 bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 transition-colors text-sm font-medium"
                  >
                    <Eye className="w-4 h-4" />
                    <span>Voir le profil</span>
                  </button>
                  <button
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                    title="Fonctionnalité à venir"
                  >
                    <Download className="w-4 h-4" />
                    <span>Exporter PDF</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Statistiques globales */}
        {filteredEvaluations && filteredEvaluations.length > 0 && (
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-6">
              <div className="text-3xl font-bold text-violet-600 mb-1">
                {filteredEvaluations.length}
              </div>
              <div className="text-sm text-gray-600">Évaluations au total</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-6">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {filteredEvaluations.filter((e: any) => e.status === 'validated').length}
              </div>
              <div className="text-sm text-gray-600">Évaluations validées</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-6">
              <div className="text-3xl font-bold text-indigo-600 mb-1">
                {(filteredEvaluations.reduce((sum: number, e: any) => sum + (e.finalScore || 0), 0) / filteredEvaluations.length).toFixed(1)}
              </div>
              <div className="text-sm text-gray-600">Moyenne générale</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
